import i18n from "../i18n";
import type { Bench, Campaign, Project, Run } from "../api";
import { campaignBuildName } from "./campaignBuild";
import { newFailures, onlyKnown } from "./knownProblems";

export type Tone = "good" | "warn" | "bad" | "off" | "live";

export type ProductState = {
  project: Project;
  tone: Tone;
  /** One sentence: what the newest result says. */
  answer: string;
  /** The benches, in words: "1 of 2 benches online", "bench offline for 4 days". */
  benches: string;
  online: number;
  total: number;
};

const DAY = 86_400;

function failedOf(r: Run): number {
  const v = r.verdict_count ?? 0;
  if (v > 0) return Math.max(0, v - (r.v_passed ?? 0) - (r.v_xfailed ?? 0) - (r.v_skipped ?? 0));
  return r.failed_scenarios ?? 0;
}

/**
 * What a product's tile says, from data the service already sends.
 *
 * The NEWEST RESULT decides the colour: a campaign when the product has one
 * newer than its last run's day, otherwise that run. The benches are said
 * beside it, not mixed into it - an offline bench does not make a result
 * wrong, and a red result on an offline bench is still red.
 */
export function productState(
  project: Project,
  benches: Bench[],
  campaigns: Campaign[],
  runs: Run[],
  now = Date.now() / 1000,
): ProductState {
  const t = i18n.t.bind(i18n);
  const mine = benches.filter((b) => b.project_id === project.id);
  const ids = new Set(mine.map((b) => b.id));
  const online = mine.filter((b) => b.agent_alive ?? b.online).length;
  const lastSeen = Math.max(0, ...mine.map((b) => b.last_seen_at ?? 0));
  const benchWords = !mine.length
    ? t("product.noBenches")
    : online
      ? t("product.online", { online, total: mine.length })
      : lastSeen
        ? t("product.offlineFor", { count: Math.max(1, Math.round((now - lastSeen) / DAY)) })
        : t("product.offline");

  const camp = campaigns
    .filter((c) => c.project_id === project.id || ids.has(c.bench_id))
    .sort((a, b) => b.created_at - a.created_at)[0];
  // A FULL run speaks for the product; a slice speaks for the cases it ran.
  // sncf-lab-1's newest run was a five-case check (run120, clean) the day
  // after a full regression with 7 failures (run115) - reading the newest run
  // turned the card green. A slice decides only where nothing full has run.
  const mineRuns = runs
    .filter((r) => ids.has(r.bench_id))
    .sort((a, b) => (b.started_at ?? 0) - (a.started_at ?? 0));
  const run = mineRuns.find((r) => r.scope === "full") ?? mineRuns[0];

  const base = { project, benches: benchWords, online, total: mine.length };
  if (camp && (!run || camp.created_at >= (run.started_at ?? 0) - DAY)) {
    const build = camp.build_ref ? campaignBuildName(camp) : t("product.installed");
    if (camp.status === "running") return { ...base, tone: "live", answer: t("product.campRunning", { build }) };
    if (camp.status === "green") return { ...base, tone: "good", answer: t("product.campGreen", { build }) };
    if (onlyKnown(camp)) return { ...base, tone: "warn", answer: t("product.campKnown", { build, n: camp.known }) };
    const fresh = newFailures(camp);
    if (fresh) return { ...base, tone: "bad", answer: t("product.campFailed", { build, count: fresh }) };
    if (camp.lost) return { ...base, tone: "bad", answer: t("product.campLost", { build }) };
    return { ...base, tone: "warn", answer: t("product.campShort", { build }) };
  }
  if (run) {
    const f = failedOf(run);
    return f
      ? { ...base, tone: "bad", answer: t("product.runFailed", { run: run.name, count: f }) }
      : { ...base, tone: "good", answer: t("product.runClean", { run: run.name }) };
  }
  return { ...base, tone: "off", answer: t("product.nothingYet") };
}
